import { toast } from 'react-toastify'

import type { InFlightJob } from 'features/settings/features/access/lib/bulk-add-job-storage'
import type { BulkJobStatusEntry } from 'features/settings/features/access/types'
const TERMINAL_TOAST_AUTO_CLOSE_MS = 10_000

export function renderTerminalToast(
  job: InFlightJob,
  entry: BulkJobStatusEntry,
): void {
  const message = buildToastMessage(job, entry)
  // Finite auto-dismiss (feedback: sticky toasts lingered indefinitely).
  // The bell notification is the durable surface now — the toast is just
  // the ephemeral heads-up, so it can close itself like every other
  // toast in the app.
  if (
    entry.status === 'completed' &&
    entry.summary?.verification_passed === false
  ) {
    // Completed, but some intended memberships are missing: a success toast
    // here is how a partial add used to read as "all added".
    toast.warning(message, { autoClose: TERMINAL_TOAST_AUTO_CLOSE_MS })
  } else if (entry.status === 'completed') {
    toast.success(message, { autoClose: TERMINAL_TOAST_AUTO_CLOSE_MS })
  } else {
    toast.error(message, { autoClose: TERMINAL_TOAST_AUTO_CLOSE_MS })
  }
}

function buildToastMessage(
  job: InFlightJob,
  entry: BulkJobStatusEntry,
): string {
  const hubLabel = job.hubTitle || job.hubUuid
  // The poller only invokes this with terminal entries (`completed` /
  // `failed`), where the backend always populates `summary`. Defensive
  // fallback covers the pathological "terminal status but null summary"
  // shape so a transient backend bug can't crash the toast.
  const { summary } = entry
  if (entry.status === 'failed') {
    return `Bulk-add to ${hubLabel} failed: ${
      summary?.error_message ?? 'see API logs'
    }`
  }
  if (summary === null) {
    return `Bulk-add to ${hubLabel} completed.`
  }
  // Count memberships written, not accounts created: an existing account
  // that was shared is still "added", a new account whose share was skipped
  // is not.
  const parts = [`${summary.newly_connected_emails.length} members added`]
  if (summary.already_connected_emails.length > 0) {
    parts.push(`${summary.already_connected_emails.length} already members`)
  }
  if (summary.missing_emails.length > 0) {
    parts.push(`${summary.missing_emails.length} could not be added`)
  }
  if (summary.failed_emails.length > 0) {
    parts.push(`${summary.failed_emails.length} failed (IDP)`)
  }
  const accounts = ` (${summary.newly_created_count} new accounts created)`
  const verification = summary.verification_passed
    ? ''
    : '. Verification did not pass; check the member list.'
  return `Bulk-add to ${hubLabel}: ${parts.join(', ')}${accounts}${verification}`
}
